import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { FooterActionComponent } from '@shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { FuncionalidadeEnum } from '@shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '@shared/enums/usuario/modulo.enum';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import { ConfiguracaoBuscaSefazService } from '@shared/services/apis/dfe/configuracao-busca-sefaz.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { PeriodoBuscaSefaz } from '@shared/services/models/dfe/periodo-busca-sefaz.model';
import { ConfiguracaoBuscaSefazRequest } from '@shared/services/requests/dfe/configuracao-busca-sefaz.request';
import { AlertService, AlertType } from '@shared/services/alert.service';

@Component({
  selector: 'app-busca-nfe-sefaz',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTooltipModule,
    AlertCardComponent,
    FooterActionComponent,
    HeaderComponent,
    LoadingBoxComponent,
  ],
  templateUrl: './busca-nfe-sefaz.component.html',
  styleUrls: ['./busca-nfe-sefaz.component.css'],
})
export class BuscaNFeSefazComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_NotasFiscais;
  public form: FormGroup;
  public periodos: PeriodoBuscaSefaz[] = [];
  public isLoading = true;
  public isLoadingSalvar = false;
  public podeAlterar = false;
  private valoresOriginais: ConfiguracaoBuscaSefazRequest;

  constructor(
    private formBuilder: FormBuilder,
    private service: ConfiguracaoBuscaSefazService,
    private authService: AuthService,
    private alertService: AlertService,
    private router: Router,
  ) {
    this.form = this.formBuilder.group({
      isCienciaAutomatica: [false],
      isBuscaAutomatica: [false],
      idPeriodoBuscaSefaz: [1],
    });
    this.valoresOriginais = this.obterRequest();
  }

  ngOnInit(): void {
    const usuario = this.authService.getUserInfos();
    this.podeAlterar = usuario.listaPermissoes.includes(PermissaoEnum.Configuracoes_NotasFiscais_Alterar);
    if (!this.podeAlterar) this.form.disable();
    this.form.get('isBuscaAutomatica')?.valueChanges.subscribe(() => this.atualizarPeriodoHabilitado());
    this.carregar();
  }

  public salvar(): void {
    if (!this.podeAlterar || this.isLoadingSalvar) return;
    this.isLoadingSalvar = true;
    const request = this.obterRequest();
    this.service.put(request).subscribe({
      next: response => {
        this.isLoadingSalvar = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }
        this.valoresOriginais = request;
        this.alertService.show('Sucesso', 'Configurações salvas com sucesso.', AlertType.success);
      },
      error: () => {
        this.isLoadingSalvar = false;
        this.alertService.show('Erro', 'Não foi possível salvar as configurações.', AlertType.error);
      },
    });
  }

  public cancelar(): void {
    this.form.patchValue(this.valoresOriginais);
    this.router.navigate(['/configuracoes/notas-fiscais']);
  }

  private carregar(): void {
    this.service.get().subscribe({
      next: response => {
        this.isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }
        this.periodos = response.listaPeriodoBuscaSefaz || [];
        const config = response.configuracaoBuscaSefaz;
        this.form.patchValue({
          isCienciaAutomatica: !!config?.isCienciaAutomatica,
          isBuscaAutomatica: !!config?.isBuscaAutomatica,
          idPeriodoBuscaSefaz: config?.idPeriodoBuscaSefaz || 1,
        });
        this.valoresOriginais = this.obterRequest();
        this.atualizarPeriodoHabilitado();
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Não foi possível carregar as configurações.', AlertType.error);
      },
    });
  }

  private obterRequest(): ConfiguracaoBuscaSefazRequest {
    const value = this.form.getRawValue();
    return {
      isCienciaAutomatica: !!value.isCienciaAutomatica,
      isBuscaAutomatica: !!value.isBuscaAutomatica,
      idPeriodoBuscaSefaz: value.idPeriodoBuscaSefaz || 1,
    };
  }

  private atualizarPeriodoHabilitado(): void {
    const periodo = this.form.get('idPeriodoBuscaSefaz');
    if (!this.podeAlterar || !this.form.get('isBuscaAutomatica')?.value) periodo?.disable({ emitEvent: false });
    else periodo?.enable({ emitEvent: false });
  }
}
